import React from 'react';
import styled from 'styled-components';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import CtaPrimary from 'components/ui/CtaPrimary';

import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';

import { getTranslatedString } from 'helpers/i18n';
import { validEmail } from 'helpers/Validator';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';

import TenantDetailsCard from './TenantDetailsCard';
import Lottie from 'react-lottie';

const StyledSecondaryButton = styled.button`
  margin: 20px;
`;

class DuplicateTenantDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      success: false,
      editingTenant: false,
      editedEmail: '',
      confirmationType: null,
      editEmailError: null,
    };
  }

  handleClose = () => {
    this.setState({
      error: false,
      loading: false,
      success: false,
      editingTenant: false,
      editedEmail: '',
      confirmationType: null,
      editEmailError: null,
    });
    this.props.handleClose();
  };

  updateTenant = () => {
    this.setState({
      loading: true,
    });
    const { confirmationType, editedEmail } = this.state;
    let action_type = confirmationType || (this.props.merge && 'merge_tenants');
    const options = {
      email: editedEmail,
      action_type: action_type,
    };
    this.props
      .updateDuplicateTenant(this.props.duplicateTenant.id, options)
      .then(() => {
        this.handleSubmissionComplete();
      })
      .catch(() => {
        this.setState({ error: true, loading: false });
      });
  };

  modifyTenant = () => {
    const { editedEmail, editEmailError } = this.state;
    if (editEmailError || !editedEmail) return;
    this.setState({
      confirmationType: 'modify_email',
    });
  };

  handleSubmissionComplete = () => {
    this.setState({
      loading: false,
      success: true,
    });
  };

  editEmail = email => {
    this.setState({ editedEmail: email, editEmailError: validEmail(email) });
  };

  handleCancelConfirm = () => {
    this.setState({ confirmationType: null });
    if (this.props.merge) {
      this.handleClose();
    }
  };

  render() {
    if (this.state.success) {
      return this.renderSuccess();
    } else if (this.state.error) {
      return this.renderError();
    } else if (this.state.loading) {
      return this.renderLoading();
    } else if (this.state.confirmationType || this.props.merge) {
      return this.renderConfirm();
    } else {
      return this.renderModifyTenant();
    }
  }

  renderModifyTenant = () => {
    const { loading } = this.state;
    return (
      <React.Fragment>
        <DrawerHeader
          onClose={this.handleClose}
          title={getTranslatedString('landlordPortal.dashboard.modifyEmail')}
        />
        <DrawerBody>
          <TenantDetailsCard
            tenant={this.props.duplicateTenant}
            editedEmail={this.state.editedEmail}
            editEmail={this.editEmail}
            editEmailError={this.state.editEmailError}
            clearEmail={() => this.setState({ confirmationType: 'clear_email' })}
          />
        </DrawerBody>
        <DrawerFooter
          primaryActionText={getTranslatedString('landlordPortal.dashboard.saveChangeButton')}
          onPrimaryActionClick={this.modifyTenant}
          primaryActionDisabled={loading}
          secondaryActionText={getTranslatedString('landlordPortal.dashboard.cancel')}
          onSecondaryActionClick={this.handleClose}
        />
      </React.Fragment>
    );
  };

  renderConfirm = () => {
    const { confirmationType, editedEmail } = this.state;
    const { duplicateTenant } = this.props;
    let header = getTranslatedString('landlordPortal.dashboard.mergeUsersHeader');
    let bodyText = getTranslatedString('landlordPortal.dashboard.mergeBody', {
      original_tenant: this.props.originalTenant.full_name,
      duplicate_tenant: this.props.duplicateTenant.full_name,
    });
    let primaryButtonLabel = getTranslatedString('landlordPortal.dashboard.mergeDuplicateButton');

    const email = duplicateTenant.email ? duplicateTenant.email : duplicateTenant.unconfirmed_email;
    if (confirmationType === 'clear_email') {
      header = getTranslatedString('landlordPortal.dashboard.saveChangesHeader');
      bodyText = getTranslatedString('landlordPortal.dashboard.clearBody', {
        full_name: duplicateTenant.full_name,
        email: email,
      });
      primaryButtonLabel = getTranslatedString('landlordPortal.dashboard.saveChangeButton');
    } else if (confirmationType === 'modify_email') {
      header = getTranslatedString('landlordPortal.dashboard.saveChangesHeader');
      bodyText = getTranslatedString('landlordPortal.dashboard.modifyBody', {
        full_name: duplicateTenant.full_name,
        email: email,
        email_changed: editedEmail,
      });
      primaryButtonLabel = getTranslatedString('landlordPortal.dashboard.saveChangeButton');
    }

    return (
      <DrawerSuccessMessages
        widthPercentage="85%"
        headerText={header}
        bodyText={bodyText}
        cta={<CtaPrimary onClick={this.updateTenant}>{primaryButtonLabel}</CtaPrimary>}
        secondaryCta={
          <StyledSecondaryButton className="link-button" onClick={this.handleCancelConfirm}>
            {getTranslatedString('cancel')}
          </StyledSecondaryButton>
        }
      />
    );
  };

  renderError = () => {
    const defaultOptions = {
      loop: false,
      autoplay: true,
      animationData: errorAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={120} width={120} />}
        headerText={getTranslatedString('globalDrawer.errorHeader', {
          name: this.props.duplicateTenant.full_name,
        })}
        cta={
          <CtaPrimary onClick={() => this.setState({ error: false, confirmationType: null })}>
            {getTranslatedString('globalDrawer.goBack')}
          </CtaPrimary>
        }
      />
    );
  };

  renderSuccess = () => {
    const defaultOptions = {
      loop: false,
      autoplay: true,
      animationData: successAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('addProspectDrawer.successHeader', {
          first_name: this.props.duplicateTenant.first_name,
          last_name: this.props.duplicateTenant.last_name,
        })}
        bodyText={getTranslatedString('globalDrawer.successBody')}
        cta={<CtaPrimary onClick={this.handleClose}>{getTranslatedString('close')}</CtaPrimary>}
      />
    );
  };

  renderLoading = () => {
    const defaultOptions = {
      loop: true,
      autoplay: true,
      animationData: loadingAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('landlordPortal.dashboard.loadingModify')}
        bodyText={getTranslatedString('globalDrawer.loadingBody')}
      />
    );
  };
}

export default DuplicateTenantDrawer;
